import { Link } from "react-router-dom";
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { Info } from "lucide-react";
import { stateColor } from "./theme";

// Maps each metric label to its /learn slug + a 1-line tooltip blurb. If a
// label isn't in this map, the info tooltip is omitted gracefully.
const METRIC_LEARN = {
  "EBITDA": { slug: "ebitda", hint: "Operating cash earnings before interest, taxes, depreciation, amortization." },
  "Gross Margin": { slug: "gross-margin", hint: "Revenue minus direct costs (COGS), as a % of revenue." },
  "Operating Margin": { slug: "operating-margin", hint: "Profit after COGS and operating expenses, as a % of revenue." },
  "Net Margin": { slug: "net-margin", hint: "Bottom line — what you keep after every cost, as a % of revenue." },
  "DSCR": { slug: "dscr", hint: "EBITDA / annual debt service. Lenders typically want ≥ 1.25×." },
  "Interest Coverage": { slug: "interest-coverage", hint: "EBIT / interest expense. Lenders want ≥ 2.0× minimum." },
  "Debt / EBITDA": { slug: "debt-to-ebitda", hint: "Years of EBITDA needed to repay all debt. Below 3× is healthy." },
  "Debt / Equity": { slug: "debt-to-equity", hint: "How much of the business is funded by debt vs owners." },
  "Debt / Assets": { slug: "debt-to-assets", hint: "Share of total assets funded by debt rather than equity." },
  "Current Ratio": { slug: "current-ratio", hint: "Current assets / current liabilities. Above 1.5× is comfortable." },
  "Quick Ratio": { slug: "quick-ratio", hint: "Stricter liquidity test — excludes inventory. Above 1.0× is healthy." },
  "Inventory Turnover": { slug: "inventory-turnover", hint: "How many times inventory cycles through the business per year." },
};

export default function Metric({ label, value, sub, state = "neutral" }) {
  const dot = stateColor[state];
  const learn = METRIC_LEARN[label];
  return (
    <div
      className="bg-white border border-[#E5E0D8] rounded-xl p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-sm"
      data-testid={`metric-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C665D] truncate">{label}</p>
          {learn && (
            <TooltipProvider delayDuration={150}>
              <UITooltip>
                <TooltipTrigger asChild>
                  <Link
                    to={`/learn/${learn.slug}`}
                    className="text-[#5C665D] hover:text-[#1F382E] transition-colors no-print shrink-0"
                    aria-label={`Learn more about ${label}`}
                    data-testid={`metric-info-${learn.slug}`}
                  >
                    <Info size={11} strokeWidth={1.75} />
                  </Link>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  className="bg-[#1F382E] text-[#F7F5F0] max-w-[240px] text-[11px] leading-snug py-2 px-3 rounded-md"
                >
                  <p className="font-medium mb-1">{label}</p>
                  <p className="text-[#D4C9B6]">{learn.hint}</p>
                  <p className="text-[10px] text-[#C26D5C] mt-1.5 uppercase tracking-wider">
                    Click to learn more →
                  </p>
                </TooltipContent>
              </UITooltip>
            </TooltipProvider>
          )}
        </div>
        <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: dot }} />
      </div>
      <p className="font-serif-display text-3xl text-[#1F382E] tabular-nums mt-2 leading-none">{value}</p>
      {sub && <p className="text-xs text-[#5C665D] mt-2">{sub}</p>}
    </div>
  );
}
